// ============================================
// COMPONENT: DashboardView
// ============================================
import React, { useMemo } from 'react';
import { Activity, LogOut, Settings, ArrowLeftRight, UserCog } from 'lucide-react';

// ------------------------------------------------
// Helpers
// ------------------------------------------------
const COMPANY_NAMES = {}; // dinámico via prop companies

const ZONES = ['Norte', 'Centro', 'Centro-Sur', 'Sur'];

const PLANT_ZONES = {
  // Norte
  'Arica AB': 'Norte',
  'Arica B': 'Norte',
  // Centro
  'Casablanca': 'Centro',
  'Viña del Mar': 'Centro',
  'Lampa': 'Centro',
  'Melipilla': 'Centro',
  'Rancagua': 'Centro',
  'SVTT': 'Centro',
  'Independencia': 'Centro',
  'Pudahuel': 'Centro',
  'San Bernardo': 'Centro',
  'Renca A': 'Centro',
  'La Reina B': 'Centro',
  // Centro-Sur
  'Curicó': 'Centro-Sur',
  'Constitución': 'Centro-Sur',
  'Parral': 'Centro-Sur',
  // Sur
  'San Pedro de la Paz': 'Sur',
  'Los Ángeles': 'Sur',
  'Coronel': 'Sur',
  'Chillan': 'Sur',
  'Quirihue': 'Sur',
};

const getZone = (plant) => PLANT_ZONES[plant.name] || 'Otras';

const groupByZone = (plants) => {
  const groups = {};
  plants.forEach(plant => {
    const zone = getZone(plant);
    if (!groups[zone]) groups[zone] = [];
    groups[zone].push(plant);
  });
  // Ordenar zonas: Norte, Centro, Centro-Sur, Sur, luego cualquier otra
  const order = [...ZONES, 'Otras'];
  return order.filter(z => groups[z]).map(z => ({ zone: z, plants: groups[z] }));
};

// ------------------------------------------------
// Component
// ------------------------------------------------
const DashboardView = ({
  user,
  plants,
  equipments,
  faults = [],
  companies = [],
  selectedCompany,      // null = global | 1 | 2
  onSelectPlant,
  onGoToAdmin,
  onGoToFaultHistory,
  onGoToCalibrationHistory,
  onGoToAssets,
  onChangeCompany,
  onLogout,
  onOpenMyData,
  getCalibrationStatus,
}) => {
  const companyNamesMap = Object.fromEntries(companies.map(c => [c.id, c.name]));
  // Filtrar plantas/equipos según empresa seleccionada
  const visiblePlants = useMemo(() => {
    const active = plants.filter(p => p.active !== false);
    if (selectedCompany == null) return active;       // null = global
    return active.filter(p => p.company_id === selectedCompany);
  }, [plants, selectedCompany]);

  const visibleEquipments = useMemo(() => {
    if (selectedCompany == null) return equipments;
    return equipments.filter(e => e.company_id === selectedCompany);
  }, [equipments, selectedCompany]);

  const stats = useMemo(() => {
    const totalEquip = visibleEquipments.length;
    const operativeEquip = visibleEquipments.filter(e => e.status === 'operativo').length;
    const linesWithEquip = new Set(visibleEquipments.map(e => `${e.plantId}-${e.line}`)).size;
    const availability = totalEquip > 0 ? Math.round((operativeEquip / totalEquip) * 100) : 0;
    const expiredCalibrations = visibleEquipments.filter(
      e => getCalibrationStatus && getCalibrationStatus(e.id) === 'expired'
    ).length;
    const activeFaults = faults.filter(f => {
      if (f.estado === 'resuelta') return false;
      if (selectedCompany == null) return true;
      return f.company_id === selectedCompany;
    }).length;

    return {
      active: visiblePlants.length,
      linesWithEquip,
      operativeEquip,
      totalEquip,
      availability,
      activeFaults,
      expiredCalibrations,
    };
  }, [visiblePlants, visibleEquipments, faults, selectedCompany, getCalibrationStatus]);

  const zoneGroups = useMemo(() => groupByZone(visiblePlants), [visiblePlants]);

  const companyLabel = selectedCompany == null
    ? 'Vista Global — Todas las empresas'
    : (companyNamesMap[selectedCompany] || COMPANY_NAMES[selectedCompany] || `Empresa ${selectedCompany}`);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-800 px-4 py-4 border-b border-slate-700 w-full overflow-x-hidden">
        <div className="flex flex-wrap justify-between items-center gap-y-2 w-full min-w-0">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Activity size={24} className="text-cyan-400 shrink-0" />
            <div className="text-white min-w-0">
              <h1 className="text-base sm:text-xl font-bold">Sistema PRT</h1>
              <p className="text-xs text-slate-400 truncate max-w-[150px] sm:max-w-none">{user.email} · {user.role}</p>
            </div>
            {/* Empresa activa */}
            <div className="hidden md:flex items-center gap-2 ml-4 px-3 py-1 bg-slate-700 rounded-lg">
              <span className="text-cyan-400 text-xs font-semibold">{companyLabel}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm text-cyan-400 hidden lg:block">
              {new Date().toLocaleString('es-CL')}
            </p>

            {/* Cambiar empresa — scope global o Gerente General */}
            {(user.scope === 'global' || user.role === 'Gerente General') && (
              <button
                onClick={onChangeCompany}
                className="p-2 sm:px-3 sm:py-2 text-slate-300 hover:text-white flex items-center gap-1 text-sm"
                title="Cambiar empresa"
              >
                <ArrowLeftRight size={15} />
                <span className="hidden sm:inline">Cambiar empresa</span>
              </button>
            )}

            {(user.role === 'Administrador' || user.role === 'Gerente' || user.role === 'Gerente General') && (
              <button
                onClick={onGoToAdmin}
                className="p-2 sm:px-3 sm:py-2 text-slate-300 hover:text-white flex items-center gap-1 text-sm"
              >
                <Settings size={15} />
                <span className="hidden sm:inline">Admin</span>
              </button>
            )}

            {onOpenMyData && (
              <button
                onClick={onOpenMyData}
                className="p-2 sm:px-3 sm:py-2 text-slate-300 hover:text-white flex items-center gap-1 text-sm"
                title="Mis Datos"
              >
                <UserCog size={15} />
                <span className="hidden sm:inline">Mis Datos</span>
              </button>
            )}

            <button
              onClick={onLogout}
              className="p-2 sm:px-3 sm:py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1 text-sm"
            >
              <LogOut size={15} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="p-6">
        {/* Stats */}
        <div className="grid md:grid-cols-5 gap-4 mb-6">
          <StatCard title="Plantas Activas"       value={stats.active}                              color="green" />
          <StatCard title="Líneas con Equipos"    value={stats.linesWithEquip}                      color="cyan"  />
          <StatCard title="Equipos Operativos"    value={`${stats.operativeEquip}/${stats.totalEquip}`} color="white" />
          <StatCard title="Disponibilidad"        value={`${stats.availability}%`}                  color="cyan"  />
          <div className="space-y-2">
            <div onClick={onGoToFaultHistory} className="cursor-pointer hover:scale-105 transition-transform">
              <StatCard title="Fallas Activas"           value={stats.activeFaults}          color="red" border="red" />
            </div>
            <div onClick={onGoToCalibrationHistory} className="cursor-pointer hover:scale-105 transition-transform">
              <StatCard title="Calibraciones Vencidas"   value={stats.expiredCalibrations}   color="red" border="red" />
            </div>
          </div>
        </div>

        {/* Plants agrupadas por zona */}
        {zoneGroups.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400 text-lg">No hay plantas registradas</p>
            {(user.role === 'Administrador' || user.role === 'Gerente' || user.role === 'Gerente General') && (
              <button
                onClick={onGoToAdmin}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Ir a Administración
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {zoneGroups.map(({ zone, plants: zonePlants }) => (
              <div key={zone}>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-slate-300 font-semibold text-sm uppercase tracking-widest">
                    Zona {zone}
                  </h2>
                  <div className="flex-1 h-px bg-slate-700" />
                  <span className="text-slate-500 text-xs">{zonePlants.length} planta{zonePlants.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {zonePlants.map(plant => (
                    <PlantCard
                      key={plant.id}
                      plant={plant}
                      equipments={equipments.filter(e => e.plantId === plant.id)}
                      faults={faults.filter(f => f.plant_id === plant.id)}
                      onClick={() => onSelectPlant(plant)}
                      getCalibrationStatus={getCalibrationStatus}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ------------------------------------------------
// Sub-components
// ------------------------------------------------
const StatCard = ({ title, value, color, border = 'slate' }) => {
  const colorMap  = { green: 'text-green-400', cyan: 'text-cyan-400', white: 'text-white', red: 'text-red-400' };
  const borderMap = { slate: 'border-slate-700', red: 'border-red-500' };
  return (
    <div className={`bg-slate-800 rounded-lg p-6 border ${borderMap[border]}`}>
      <p className="text-slate-400 text-sm mb-2">{title}</p>
      <p className={`text-4xl font-bold ${colorMap[color]}`}>{value}</p>
    </div>
  );
};

const PlantCard = ({ plant, equipments, faults = [], onClick, getCalibrationStatus }) => {
  const operativeCount = equipments.filter(e => e.status === 'operativo').length;
  const totalCount     = equipments.length;
  const linesWithEquip = [...new Set(equipments.map(e => e.line))].sort();
  const availability   = totalCount > 0 ? (operativeCount / totalCount) * 100 : 0;

  const hasGeneralImpactFault = faults.some(
    f => f.equipo_afectado === 'General Planta' && f.estado !== 'resuelta' && f.estado !== 'cerrada'
  );

  const getBarColor = () => {
    if (hasGeneralImpactFault)                    return 'bg-red-500';
    if (operativeCount === totalCount && totalCount > 0) return 'bg-green-500';
    if (operativeCount > totalCount / 2)          return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div
      onClick={onClick}
      className={`rounded-lg p-5 border transition-all cursor-pointer ${
        hasGeneralImpactFault
          ? 'bg-red-900 border-red-500 hover:border-red-400'
          : 'bg-slate-800 border-slate-700 hover:border-cyan-500'
      }`}
    >
      <h3 className="text-white font-bold text-lg mb-4">{plant.name}</h3>

      {linesWithEquip.length === 0 ? (
        <div className="text-slate-400 text-center py-4">
          <p className="text-sm">Sin equipos</p>
        </div>
      ) : (
        <>
          <div
            className="grid gap-2 mb-4"
            style={{ gridTemplateColumns: `repeat(${Math.min(linesWithEquip.length, 4)}, 1fr)` }}
          >
            {linesWithEquip.map(lineNum => {
              const lineEquip     = equipments.filter(e => e.line === lineNum);
              const lineOperative = lineEquip.filter(e => e.status === 'operativo').length;
              const hasFailure    = hasGeneralImpactFault ||
                lineEquip.some(e => e.status === 'falla') ||
                lineEquip.some(e => getCalibrationStatus && getCalibrationStatus(e.id) === 'expired');

              return (
                <div key={lineNum} className="text-center">
                  <p className="text-slate-400 text-xs mb-2 font-bold">{lineNum}</p>
                  <div className={`h-10 rounded flex items-center justify-center ${hasFailure ? 'bg-red-500' : 'bg-green-500'}`}>
                    <div className={`w-4 h-4 rounded-full ${hasFailure ? 'bg-red-200 animate-pulse' : 'bg-green-200'}`} />
                  </div>
                  <p className="text-slate-300 text-xs mt-1 font-semibold">
                    {lineOperative}/{lineEquip.length}
                  </p>
                </div>
              );
            })}
          </div>

          <p className="text-slate-300 font-semibold border-t border-slate-700 pt-3">
            Total: {operativeCount}/{totalCount} operativos
          </p>
          <div className="mt-2 bg-slate-700 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all ${getBarColor()}`} style={{ width: `${availability}%` }} />
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardView;
